// components/onboarding/wizard/OnboardingWizard.jsx
//
// The guided, one-step-at-a-time onboarding flow. Reveals a single step at a time (Profile,
// Business, Documents, Bank, Review) with autosave and a per-step Skip - the antidote to the old
// "everything on one screen" checklist.
//
// Two presentations share the exact same logic:
//   - variant="page"    → immersive TWO-PANE layout (sticky vertical stepper + context on the left,
//                          the active step's fields on the right). Uses the horizontal space and
//                          keeps everything on-screen with minimal vertical scrolling.
//   - variant="overlay" → compact stacked modal (top horizontal rail), shown at the order submit-gate.
// All data/persistence comes from useOnboardingWizard.

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useOnboardingWizard from '../../../hooks/useOnboardingWizard';
import { LottieLoader } from '../OnboardingDecor';
import {
  Spinner, ProfileStepBody, BusinessStepBody, DocumentsStepBody, BankStepBody, ReviewStepBody,
} from './steps';

const STEPS = [
  { key: 'PROFILE', label: 'Profile & address', desc: 'Pickup address' },
  { key: 'BUSINESS', label: 'Business & PAN', desc: 'PAN / GST / Aadhaar' },
  { key: 'DOCUMENTS', label: 'Documents', desc: 'KYC upload' },
  { key: 'BANK', label: 'Bank & wallet', desc: 'Optional', optional: true },
  { key: 'REVIEW', label: 'Review & submit', desc: 'Submit for review' },
];
const INDEX_OF = STEPS.reduce((m, s, i) => { m[s.key] = i; return m; }, {});

function StepCircle({ done, active, index }) {
  return (
    <span className="relative flex h-8 w-8 items-center justify-center">
      {active && !done && (
        <motion.span
          className="absolute inset-0 rounded-full bg-blue-400/30"
          animate={{ scale: [1, 1.6], opacity: [0.55, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      <motion.span
        initial={false}
        animate={done ? { scale: [1, 1.25, 1] } : { scale: 1 }}
        transition={{ duration: 0.4 }}
        className={`relative flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ring-2 transition ${
          done
            ? 'bg-green-500 text-white ring-green-500'
            : active
              ? 'bg-blue-600 text-white ring-blue-600 shadow-lg shadow-blue-500/30'
              : 'bg-white text-gray-400 ring-gray-200 group-hover:ring-blue-300'
        }`}
      >
        {done ? '✓' : index + 1}
      </motion.span>
    </span>
  );
}

function ProgressBar({ percent }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
      <motion.div
        className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"
        initial={false}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      />
    </div>
  );
}

// Compact horizontal rail (overlay + mobile).
function ProgressRail({ stepIndex, completedMap, percent, onJump }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your setup</span>
        <span className="text-xs font-semibold text-gray-700">{percent}% complete</span>
      </div>
      <div className="mb-4"><ProgressBar percent={percent} /></div>
      <ol className="flex items-center gap-1">
        {STEPS.map((s, i) => {
          const done = completedMap[s.key];
          const active = i === stepIndex;
          return (
            <li key={s.key} className="flex flex-1 items-center">
              <button
                type="button" onClick={() => onJump(i)} aria-current={active ? 'step' : undefined}
                className="group flex w-full flex-col items-center gap-1.5" title={s.label}
              >
                <StepCircle done={done} active={active} index={i} />
                <span className={`hidden text-[11px] font-medium sm:block ${active ? 'text-blue-700' : 'text-gray-400'}`}>{s.label.split(' ')[0]}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// Vertical stepper for the page left pane.
function VerticalSteps({ stepIndex, completedMap, onJump }) {
  return (
    <ol className="mt-6 space-y-1">
      {STEPS.map((s, i) => {
        const done = completedMap[s.key];
        const active = i === stepIndex;
        return (
          <li key={s.key}>
            <button
              type="button" onClick={() => onJump(i)} aria-current={active ? 'step' : undefined}
              className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition ${
                active ? 'bg-white shadow-sm ring-1 ring-blue-100' : 'hover:bg-white/70'
              }`}
            >
              <StepCircle done={done} active={active} index={i} />
              <span className="min-w-0">
                <span className={`flex items-center gap-1.5 text-sm font-semibold ${active ? 'text-gray-900' : done ? 'text-gray-700' : 'text-gray-500'}`}>
                  {s.label}
                  {s.optional && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-400">Optional</span>}
                </span>
                <span className="block truncate text-[11px] text-gray-400">{done ? 'Completed' : s.desc}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function OnboardingWizard({
  variant = 'page', welcome = false, heading = 'Let’s finish your setup', subheading, onExit, onComplete,
}) {
  const wiz = useOnboardingWizard();
  const [stepIndex, setStepIndex] = useState(0);
  const initialized = useRef(false);

  // Open on the first incomplete step once progress is known (then let the user navigate freely).
  useEffect(() => {
    if (wiz.loading || initialized.current) return;
    const next = ['PROFILE', 'BUSINESS', 'DOCUMENTS', 'BANK'].find((k) => !wiz.phaseComplete[k]);
    setStepIndex(next ? INDEX_OF[next] : INDEX_OF.REVIEW);
    initialized.current = true;
  }, [wiz.loading, wiz.phaseComplete]);

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));
  const jumpTo = (target) => setStepIndex(typeof target === 'number' ? target : (INDEX_OF[target] ?? 0));

  const key = STEPS[stepIndex].key;
  const busy = wiz.saving === key || (key === 'REVIEW' && wiz.submitting);
  const f = wiz.form || {};
  // Bank is optional: only persist (and validate) when the merchant actually entered something.
  const bankHasInput = !!((f.accNo || '').trim() || (f.ifsc || '').trim() || (f.accHolder || '').trim() || (f.bankName || '').trim() || (f.branch || '').trim());

  const handlePrimary = async () => {
    if (key === 'PROFILE') { if (await wiz.saveProfile()) goNext(); return; }
    if (key === 'BUSINESS') { if (await wiz.saveBusiness()) goNext(); return; }
    if (key === 'BANK') { if (!bankHasInput) { goNext(); return; } if (await wiz.saveBank()) goNext(); return; }
    if (key === 'DOCUMENTS') { goNext(); return; }
    if (key === 'REVIEW') { if (await wiz.submit()) onComplete?.(); }
  };

  const primaryLabel =
    key === 'REVIEW' ? 'Submit for verification'
    : key === 'DOCUMENTS' ? 'Continue'
    : key === 'BANK' ? (bankHasInput ? 'Save & continue' : 'Continue')
    : 'Save & continue';
  const primaryDisabled = busy || (key === 'REVIEW' && !wiz.requiredDone);
  const canSkip = key === 'PROFILE' || key === 'BUSINESS';

  const completedMap = wiz.phaseComplete || {};
  const percent = Math.max(0, Math.min(100, wiz.completionPercent || 0));
  const isPage = variant === 'page';

  if (wiz.loading) {
    return (
      <div className={`mx-auto w-full ${isPage ? 'max-w-5xl' : 'max-w-3xl'}`}>
        <div className="flex min-h-[300px] items-center justify-center overflow-hidden rounded-3xl border border-gray-100 bg-white/90 shadow-xl backdrop-blur-xl">
          <LottieLoader size={88} message="Loading your setup…" />
        </div>
      </div>
    );
  }

  const stepBodyNode = wiz.loadError ? (
    <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <span>{wiz.loadError}</span>
      <button onClick={wiz.reload} className="font-semibold underline">Retry</button>
    </div>
  ) : (
    <AnimatePresence mode="wait">
      <motion.div
        key={stepIndex}
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -24 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        {key === 'PROFILE' && <ProfileStepBody wiz={wiz} />}
        {key === 'BUSINESS' && <BusinessStepBody wiz={wiz} />}
        {key === 'DOCUMENTS' && <DocumentsStepBody wiz={wiz} />}
        {key === 'BANK' && <BankStepBody wiz={wiz} />}
        {key === 'REVIEW' && <ReviewStepBody wiz={wiz} onJumpTo={jumpTo} />}
      </motion.div>
    </AnimatePresence>
  );

  const messagesNode = wiz.sectionMsg[key] && !wiz.sectionError[key]
    ? <p className="mt-3 text-xs font-medium text-green-600">{wiz.sectionMsg[key]}</p>
    : null;

  const footerNode = (
    <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-4 sm:px-8">
      <div className="flex items-center gap-1">
        {stepIndex > 0 && (
          <button type="button" onClick={goBack} className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100">
            ← Back
          </button>
        )}
        {!isPage && onExit && (
          <button type="button" onClick={onExit} className="inline-flex items-center rounded-xl px-3 py-2.5 text-sm font-medium text-gray-400 hover:text-gray-600">
            I’ll finish later
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canSkip && (
          <button type="button" onClick={goNext} className="inline-flex items-center rounded-xl px-3.5 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-100">
            Skip for now
          </button>
        )}
        <button
          type="button" onClick={handlePrimary} disabled={primaryDisabled}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all ${
            primaryDisabled
              ? 'cursor-not-allowed bg-gray-300'
              : key === 'REVIEW'
                ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
          }`}
        >
          {busy ? (<><Spinner /> {key === 'REVIEW' ? 'Submitting…' : 'Saving…'}</>) : (<>{primaryLabel} {key !== 'REVIEW' && '→'}</>)}
        </button>
      </div>
    </div>
  );

  // ── Overlay: compact stacked modal ────────────────────────────────────────
  if (!isPage) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.35, ease: 'easeOut' }}
          className="overflow-hidden rounded-3xl border border-gray-100 bg-white/95 shadow-xl backdrop-blur-xl"
        >
          <div className="border-b border-gray-100 px-5 pb-4 pt-5">
            <ProgressRail stepIndex={stepIndex} completedMap={completedMap} percent={percent} onJump={jumpTo} />
          </div>
          <div className="px-6 py-6 sm:px-8">{stepBodyNode}{messagesNode}</div>
          {footerNode}
        </motion.div>
      </div>
    );
  }

  // ── Page: immersive two-pane layout ───────────────────────────────────────
  return (
    <div className="mx-auto w-full max-w-5xl">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="grid overflow-hidden rounded-3xl border border-gray-100 bg-white/90 shadow-2xl shadow-slate-200/50 backdrop-blur-xl lg:grid-cols-[290px_1fr]"
      >
        {/* Left pane - context + vertical stepper (sticky, doesn't scroll) */}
        <aside className="hidden flex-col justify-between border-r border-gray-100 bg-gradient-to-b from-slate-50 via-white to-blue-50/50 p-6 lg:flex">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Account setup</p>
            <h2 className="mt-1 text-xl font-bold leading-tight text-gray-900">{heading}</h2>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>{subheading || 'Progress saved automatically'}</span>
              <span className="font-semibold text-gray-700">{percent}%</span>
            </div>
            <div className="mt-1.5"><ProgressBar percent={percent} /></div>
            <VerticalSteps stepIndex={stepIndex} completedMap={completedMap} onJump={jumpTo} />
          </div>
          <div className="mt-6">
            {welcome && (
              <div className="mb-3 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs font-medium text-green-800">
                <span aria-hidden="true">🎉</span> Account created - nice work!
              </div>
            )}
            {onExit && (
              <button type="button" onClick={onExit} className="text-sm font-medium text-gray-400 hover:text-gray-600">
                I’ll finish later →
              </button>
            )}
          </div>
        </aside>

        {/* Right pane - the active step */}
        <div className="flex min-h-[540px] flex-col">
          <div className="px-6 pt-6 lg:hidden">
            <h2 className="text-xl font-bold text-gray-900">{heading}</h2>
            <p className="mt-0.5 text-sm text-gray-500">{subheading || 'Progress saved automatically'}</p>
          </div>
          <div className="border-b border-gray-100 px-6 pb-4 pt-4 lg:hidden">
            <ProgressRail stepIndex={stepIndex} completedMap={completedMap} percent={percent} onJump={jumpTo} />
          </div>
          <div className="flex-1 px-6 py-7 sm:px-8">
            {stepBodyNode}
            {messagesNode}
          </div>
          {footerNode}
        </div>
      </motion.div>
    </div>
  );
}
